
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { cn } from '@/lib/utils';

interface AdvanceRequestSummary {
  id: string;
  companyId: string;
  companyTaxId: string | null;
  companyBusinessName: string | null;
  status: string;
  totalAmount: string | null;
  discountAmount: string | null;
  stampTaxAmount: string | null;
  netAmount: string | null;
  monthlyRate: string | null;
  rejectionReason: string | null;
  resolutionAt: string | null;
  createdAt: string;
}

const statusConfig: Record<string, { label: string; color: string; dot: string }> = {
  requested: { label: 'Pendiente', color: 'text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500' },
  approved: { label: 'Aprobada', color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400', dot: 'bg-emerald-500' },
  disbursed: { label: 'Desembolsada', color: 'text-sky-700 bg-sky-50 dark:bg-sky-900/30 dark:text-sky-400', dot: 'bg-sky-500' },
  in_collection: { label: 'En cobro', color: 'text-violet-700 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400', dot: 'bg-violet-500' },
  collected: { label: 'Cobrada', color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400', dot: 'bg-emerald-500' },
  uncollectible: { label: 'Incobrable', color: 'text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400', dot: 'bg-red-500' },
  cancelled: { label: 'Cancelada', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400', dot: 'bg-slate-500' },
};

function formatMoney(val: string | null) {
  if (!val) return '—';
  return `$${Number(val).toLocaleString('es-CL', { minimumFractionDigits: 0 })}`;
}

const filters = [
  { value: '', label: 'Todas', icon: 'list' },
  { value: 'requested', label: 'Pendientes', icon: 'schedule' },
  { value: 'approved', label: 'Aprobadas', icon: 'check_circle' },
  { value: 'disbursed', label: 'Desembolsadas', icon: 'payments' },
  { value: 'in_collection', label: 'En cobro', icon: 'sync' },
  { value: 'collected', label: 'Cobradas', icon: 'task_alt' },
  { value: 'uncollectible', label: 'Incobrables', icon: 'dangerous' },
  { value: 'cancelled', label: 'Canceladas', icon: 'cancel' },
];

function SkeletonRows() {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
      {Array.from({ length: 7 }).map((_, j) => (
        <td key={j} className="px-4 py-3.5">
          <div className="h-3.5 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" style={{ width: `${40 + Math.random() * 50}%` }} />
        </td>
      ))}
    </tr>
  ));
}

export default function SolicitudesPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<AdvanceRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('requested');

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  async function loadRequests() {
    setLoading(true);
    try {
      let url = getAdminEndpointUrl('advanceRequests', 'list');
      if (statusFilter) url += (url.includes('?') ? '&' : '?') + `status=${statusFilter}`;
      const data = await adminHttpClient.get<AdvanceRequestSummary[]>(url);
      setRequests(data);
    } catch (err) {
      console.error('Error cargando solicitudes:', err);
    } finally {
      setLoading(false);
    }
  }

  const pendingCount = requests.filter((r) => r.status === 'requested').length;

  return (
    <AdminLayout
      title="Solicitudes de Adelanto"
      subtitle={pendingCount > 0 ? `${pendingCount} pendiente${pendingCount > 1 ? 's' : ''} de revision` : 'Todas las solicitudes al dia'}
    >
      <div className="space-y-5">
        {/* Filters */}
        <div className="flex items-center gap-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-1 w-fit">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150',
                statusFilter === f.value
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              <span className="material-symbols-outlined text-[14px]">{f.icon}</span>
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">ID</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Empresa</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3 text-right">Monto Total</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3 text-right">Descuento</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3 text-right">Monto Neto</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Estado</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <SkeletonRows />
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16">
                    <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600">inbox</span>
                    <p className="text-sm text-slate-500 mt-2">No hay solicitudes</p>
                  </td>
                </tr>
              ) : (
                requests.map((ar) => {
                  const sc = statusConfig[ar.status] || statusConfig.requested;
                  return (
                    <tr
                      key={ar.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/solicitudes/detalle?id=${ar.id}`)}
                    >
                      <td className="px-4 py-3.5 font-mono text-xs text-slate-500">{ar.id.slice(0, 10)}...</td>
                      <td className="px-4 py-3.5">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{ar.companyBusinessName || '—'}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{ar.companyTaxId || ''}</div>
                      </td>
                      <td className="px-4 py-3.5 text-right text-sm font-mono text-slate-700 dark:text-slate-300">{formatMoney(ar.totalAmount)}</td>
                      <td className="px-4 py-3.5 text-right text-sm font-mono text-red-600 dark:text-red-400">{formatMoney(ar.discountAmount)}</td>
                      <td className="px-4 py-3.5 text-right text-sm font-mono font-semibold text-slate-900 dark:text-white">{formatMoney(ar.netAmount)}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${sc.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{new Date(ar.createdAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
